import {
  ListAlt,
  Download,
  Save,
  ArrowBack,
  Loop,
  Check,
  Close,
  Cancel,
} from '@mui/icons-material';
import {
  Paper,
  Grid,
  Typography,
  Autocomplete,
  TextField,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import VMasker from 'vanilla-masker';
import {
  PageWrapper,
  Input,
  Checkbox,
  Button,
  DatePicker,
  CEP,
  FileUpload,
  Modal,
} from '../../../components';

import {
  CREDENTIALS_MANAGEMENT,
  CREDENTIALS_CREATE,
  CREDENTIALS_UPDATE,
} from '../../../routes/constants';
import {
  useDispatch,
  useSelector,
  fetchCredentialsView,
  fetchCredentialsUploadDocument,
  fetchCredentialsDownloadDocument,
  fetchCredentialsRenew,
  fetchCredentialsExport,
  clearCredentials,
  setCredentials,
  fetchCredentialsCPF,
  credentialsSlice,
  fetchCredentialsUpdate,
  fetchCredentialsCreate,
  fetchCredentialsFilters,
} from '../../../stores';
import { theme } from '../../../theme';
import { handleErrors } from '../../../utils/Helpers';

import useResponsiveMode from '../../../utils/useResponsiveMode';

import { Export } from '../Export';
import { DeactivateCredentialModal } from './DeactivateCredentialModal';
import { createValidation, updateValidation } from './validations';

const PHONE_MAX_LENGTH = 15;
const DOCUMENT_MAX_LENGTH = 14;

export const CredentialsUpsert: React.FC = () => {
  const responsiveMode = useResponsiveMode();

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const dispatch = useDispatch();

  const [credentialsFormErrors, setCredentialsFormErrors] = useState<{
    [key: string]: string;
  }>({});
  const [canPostalCodeBeFilled, setCanPostalCodeBeFilled] = useState<boolean>(
    location.pathname == CREDENTIALS_CREATE,
  );
  const [isRenewModalOpen, setIsRenewModalOpen] = useState<boolean>(false);
  const [renewDate, setRenewDate] = useState<string>('');
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] =
    useState<boolean>(false);
  const [uploadFormKey, setUploadFormKey] = useState<number>(Date.now());
  const [fileForm, setFileForm] = useState<FormData>();
  const [file, setFile] = useState<File>();

  const routes = useSelector(
    (state) => state.credentialsFiltersReducer.filters?.routes || [],
  );
  const { isCredentialsLoading, isCredentialsCPFLoading } = useSelector(
    (state) => state.credentialsReducer,
  );
  const credential = useSelector((state) => state.credentialsReducer);

  useEffect(() => {
    if (location.pathname == CREDENTIALS_CREATE) {
      dispatch(clearCredentials());
      dispatch(fetchCredentialsFilters());
    }
  }, [location.pathname, dispatch]);

  useEffect(() => {
    if (location.pathname.startsWith(CREDENTIALS_UPDATE) && id) {
      dispatch(fetchCredentialsView(Number(id)));
      dispatch(fetchCredentialsFilters());
    }
  }, [location.pathname, id, dispatch]);

  useEffect(() => {
    if (
      !location.pathname.startsWith(CREDENTIALS_UPDATE) ||
      !routes.length ||
      !credential.routes?.length
    ) {
      return;
    }

    const validRouteIds = new Set(
      routes
        .filter((route: { id: string; value: string }) => route?.id != '0')
        .map((route: { id: string }) => +route.id),
    );

    const sanitizedRoutes = credential.routes.filter(
      (routeId) => routeId !== 0 && validRouteIds.has(routeId),
    );

    if (sanitizedRoutes.length !== credential.routes.length) {
      dispatch(setCredentials({ ...credential, routes: sanitizedRoutes }));
    }
  }, [location.pathname, routes, credential.id, credential.routes, dispatch]);

  useEffect(() => {
    if (credential.documents) {
      dispatch(
        fetchCredentialsDownloadDocument({ fileName: credential.documents }),
      );
    }
  }, [credential.documents]);

  useEffect(() => {
    if (credential.holderDocument.length === 14) {
      dispatch(
        fetchCredentialsCPF({
          cpf: VMasker.toNumber(credential.holderDocument).toString(),
        }),
      );
    }
  }, [credential.holderDocument]);

  const handleUploadDocument = (
    form: FormData | undefined,
    file: File | undefined,
  ) => {
    if (file) {
      const fileSize = +(file.size / 1024 / 1024).toFixed(4);

      const MAXIMUM_ALLOWED_FILE_SIZE = 5;

      if (
        file.type !== 'application/zip' &&
        file.type !== 'application/octet-stream' &&
        file.type !== 'application/x-zip-compressed' &&
        file.type !== 'multipart/x-zip' &&
        file.type !== 'application/vnd.rar' &&
        file.type !== 'application/x-rar-compressed' &&
        file.type !== 'application/octet-stream' &&
        file.type !== 'application/x-rar'
      ) {
        return toast.error('O formato do arquivo precisa ser .zip ou .rar');
      }

      if (fileSize > MAXIMUM_ALLOWED_FILE_SIZE) {
        return toast.error(
          `O arquivo não pode exceder ${MAXIMUM_ALLOWED_FILE_SIZE} MB`,
        );
      }

      dispatch(fetchCredentialsUploadDocument(form));
    }
  };

  const handleDownloadDocument = () => {
    if (credential.documents) {
      dispatch(
        fetchCredentialsDownloadDocument({ fileName: credential.documents }),
      ).then((res) => {
        if (res.type == 'credentialsDownloadDocument/fulfilled') {
          const blob = new Blob([res.payload]);

          const anchor = document.createElement('a');
          const fileURL = URL.createObjectURL(blob);

          anchor.href = fileURL;
          anchor.setAttribute('download', credential.documents);
          anchor.click();
        }
      });
    }
  };

  const getValidRouteIds = () =>
    new Set(
      (routes || [])
        .filter((route: { id: string; value: string }) => route?.id != '0')
        .map((route: { id: string }) => +route.id),
    );

  const buildRoutesPayload = () => {
    if (credential.restricted !== 1) {
      return [];
    }

    const validRouteIds = getValidRouteIds();

    return (credential.routes || []).filter(
      (routeId) => routeId !== 0 && validRouteIds.has(routeId),
    );
  };

  const handleSubmit = () => {
    const isCreate = location.pathname === CREDENTIALS_CREATE;
    const form = {
      ...credential,
      holderDocument: VMasker.toNumber(credential.holderDocument),
      holderPhone: VMasker.toNumber(credential.holderPhone),
      expirationDate: credential.expirationDate || '',
      documents: credential.documents ?? '',
      observation: credential.observation ?? '',
      routes: buildRoutesPayload(),
    };

    const schema = isCreate ? createValidation : updateValidation;

    schema
      .validate(form, { abortEarly: false })
      .then(() => {
        setCredentialsFormErrors({});

        const apiForm = {
          expirationDate: moment(form.expirationDate).format(
            'YYYY-MM-DD HH:mm:ss',
          ),
          restricted: form.restricted,
          licensePlate: form.licensePlate,
          vehicleBrand: form.vehicleBrand,
          vehicleColor: form.vehicleColor,
          holderName: form.holderName,
          holderDocument: String(form.holderDocument),
          holderPhone: String(form.holderPhone),
          documents: form.documents || '',
          postalCode: String(form.postalCode),
          street: form.street,
          streetNumber: form.streetNumber,
          complement: form.complement?.trim() || '-',
          district: form.district,
          city: form.city,
          state: form.state,
          observation: form.observation || '',
          routes: form.routes,
        };

        if (credential.id) {
          dispatch(fetchCredentialsUpdate({ ...apiForm, id: credential.id }));
        } else {
          dispatch(fetchCredentialsCreate(apiForm)).then((res) => {
            if (res.type == 'credentialsCreate/fulfilled') {
              navigate(CREDENTIALS_MANAGEMENT);
            }
          });
        }
      })
      .catch((err) => {
        const errors = handleErrors(err);
        setCredentialsFormErrors(errors);
        const firstError = Object.values(errors)[0];

        if (firstError) {
          toast.error(String(firstError));
        }
      });
  };

  const handleRenew = () => {
    if (!renewDate) {
      return toast.error('Selecione uma data de validade.');
    }

    const params = {
      id: credential.id,
      expirationDate: `${renewDate} 23:59:59`,
    };

    dispatch(fetchCredentialsRenew(params)).then((res) => {
      if (res.type == 'credentialsRenew/fulfilled') {
        navigate(CREDENTIALS_MANAGEMENT);
      }
    });
  };

  const handleDeactivateCredential = () => {
    dispatch(credentialsSlice.actions.toggleCredentialDeactivateIsOpen());
  };

  const handleCredentialExport = () => {
    const params = {
      number: credential.number,
      licensePlate: credential.licensePlate,
      holderName: credential.holderName,
      holderDocument: credential.holderDocument,
      street: credential.street,
      routeId: (credential.routes || []).join(','),
      expirationDate: credential.expirationDate,
    };

    dispatch(fetchCredentialsExport(params)).then(
      (res: { type: string; payload: any }) => {
        if (res.type == 'credentialsExport/fulfilled') {
          const { number, userName, userDocument, observation } =
            res.payload[0];

          const exportation = renderToStaticMarkup(
            <Export
              credential={{ number, userName, userDocument, observation }}
            />,
          );

          const file = new Blob([exportation], {
            type: 'text/html;charset=UTF-8',
          });
          const fileURL = URL.createObjectURL(file);

          const anchor = document.createElement('a');

          anchor.href = fileURL;
          anchor.setAttribute('download', `comprovante-credencial-${number}`);
          anchor.click();
        }
      },
    );
  };

  return (
    <PageWrapper>
      {isCredentialsLoading ? (
        <CircularProgress />
      ) : (
        <>
          <Grid container justifyContent='space-between'>
            <Grid style={{ display: 'flex', alignItems: 'center' }}>
              <ListAlt fontSize='large' color='action' />
              <Typography
                variant='h5'
                color={theme.palette.text.main}
                padding={1}
              >
                {location.pathname == CREDENTIALS_CREATE
                  ? 'Cadastrar Credencial'
                  : 'Editar Credencial'}
              </Typography>
            </Grid>
            {location.pathname.startsWith(CREDENTIALS_UPDATE) && (
              <Grid padding={1}>
                <Button
                  fullWidth
                  variant='contained'
                  label='Geração de comprovante'
                  startIcon={<Download />}
                  onClick={handleCredentialExport}
                />
              </Grid>
            )}
          </Grid>
          <Paper style={{ width: '100%', marginTop: 15 }}>
            <Modal
              title='Renovação'
              open={isRenewModalOpen}
              onClose={() => setIsRenewModalOpen(!isRenewModalOpen)}
              children={
                <Grid container>
                  <Grid xs={12}>
                    <DatePicker
                      label='Data de validade da renovação'
                      value={renewDate}
                      onChange={(value) => setRenewDate(value)}
                    />
                  </Grid>
                  <Grid xs={6} padding={0.5}>
                    <Button
                      fullWidth
                      variant='contained'
                      label='Confirmar'
                      startIcon={<Check />}
                      onClick={handleRenew}
                    />
                  </Grid>
                  <Grid xs={6} padding={0.5}>
                    <Button
                      fullWidth
                      color='error'
                      variant='contained'
                      label='Cancelar'
                      startIcon={<Close />}
                      onClick={() => setIsRenewModalOpen(!isRenewModalOpen)}
                    />
                  </Grid>
                </Grid>
              }
            />
            <Modal
              title='Deseja selecionar este arquivo?'
              open={isFileUploadModalOpen}
              onClose={() => setIsFileUploadModalOpen(!isFileUploadModalOpen)}
              children={
                <Grid container>
                  <Grid xs={6} padding={0.5}>
                    <Button
                      fullWidth
                      variant='contained'
                      label='Confirmar'
                      startIcon={<Check />}
                      onClick={() => {
                        handleUploadDocument(fileForm, file);
                        setIsFileUploadModalOpen(!isFileUploadModalOpen);
                      }}
                    />
                  </Grid>
                  <Grid xs={6} padding={0.5}>
                    <Button
                      fullWidth
                      color='error'
                      variant='contained'
                      label='Cancelar'
                      startIcon={<Close />}
                      onClick={() => {
                        setUploadFormKey(Date.now());
                        setIsFileUploadModalOpen(!isFileUploadModalOpen);
                      }}
                    />
                  </Grid>
                </Grid>
              }
            />
            <Grid container padding={3}>
              <Typography variant='h5' color={theme.palette.text.main}>
                Informações do credenciado
              </Typography>
            </Grid>
            <Grid container>
              <Grid xs={12} md={4} padding={1}>
                <Input
                  type='text'
                  margin='normal'
                  name='holderName'
                  label='Nome'
                  value={credential.holderName}
                  onChange={(value) =>
                    dispatch(
                      setCredentials({ ...credential, holderName: value }),
                    )
                  }
                  errorMessage={credentialsFormErrors.holderName}
                />
              </Grid>

              <Grid xs={12} md={4} padding={1}>
                <CEP
                  id='credentialsUpsertCep'
                  name='postalCode'
                  label='CEP'
                  value={credential.postalCode}
                  error={!!credentialsFormErrors.postalCode}
                  helperText={credentialsFormErrors.postalCode}
                  onChange={(cep) => {
                    setCanPostalCodeBeFilled(true);

                    if (!canPostalCodeBeFilled) return;

                    if (!cep.erro) {
                      dispatch(
                        setCredentials({
                          ...credential,
                          postalCode: VMasker.toNumber(cep.cep),
                          street: cep.logradouro,
                          // complement: cep.complemento, // removed, do not auto-fill Complemento
                          district: cep.bairro,
                          city: cep.localidade,
                          state: cep.uf,
                        }),
                      );
                    }
                  }}
                />
              </Grid>

              <Grid xs={12} md={4} padding={1}>
                <Input
                  type='text'
                  margin='normal'
                  name='street'
                  label='Endereço'
                  value={credential.street}
                  onChange={(value) =>
                    dispatch(setCredentials({ ...credential, street: value }))
                  }
                  errorMessage={credentialsFormErrors.street}
                />
              </Grid>

              <Grid xs={12} md={4} padding={1}>
                <Input
                  type='text'
                  margin='normal'
                  name='holderDocument'
                  label='CPF'
                  value={
                    credential.holderDocument
                      ? VMasker.toPattern(
                          credential.holderDocument,
                          '999.999.999-99',
                        )
                      : ''
                  }
                  onChange={(value) =>
                    dispatch(
                      setCredentials({ ...credential, holderDocument: value }),
                    )
                  }
                  inputProps={{ maxLength: DOCUMENT_MAX_LENGTH }}
                  errorMessage={credentialsFormErrors.holderDocument}
                  InputProps={
                    isCredentialsCPFLoading && {
                      endAdornment: (
                        <InputAdornment position='end'>
                          <CircularProgress color='primary' size={24} />
                        </InputAdornment>
                      ),
                    }
                  }
                />
              </Grid>
              <Grid xs={12} md={4} padding={1}>
                <Input
                  type='text'
                  margin='normal'
                  label='Telefone'
                  value={
                    credential.holderPhone
                      ? VMasker.toPattern(
                          credential.holderPhone,
                          '(99) 99999-9999',
                        )
                      : ''
                  }
                  onChange={(value) =>
                    dispatch(
                      setCredentials({ ...credential, holderPhone: value }),
                    )
                  }
                  inputProps={{ maxLength: PHONE_MAX_LENGTH }}
                  errorMessage={credentialsFormErrors.holderPhone}
                />
              </Grid>

              <Grid xs={12} md={2} padding={1}>
                <Input
                  type='text'
                  margin='normal'
                  name='streetNumber'
                  label='Número'
                  value={credential.streetNumber}
                  onChange={(value) =>
                    dispatch(
                      setCredentials({ ...credential, streetNumber: value }),
                    )
                  }
                  errorMessage={credentialsFormErrors.streetNumber}
                />
              </Grid>
              <Grid xs={12} md={2} padding={1}>
                <Input
                  type='text'
                  margin='normal'
                  name='complement'
                  label='Complemento'
                  value={credential.complement}
                  onChange={(value) =>
                    dispatch(
                      setCredentials({
                        ...credential,
                        complement: value,
                      }),
                    )
                  }
                  errorMessage={credentialsFormErrors.complement}
                />
              </Grid>
              <Grid xs={12} md={4} padding={1}>
                <Input
                  type='text'
                  margin='normal'
                  name='district'
                  label='Bairro'
                  value={credential.district}
                  onChange={(value) =>
                    dispatch(setCredentials({ ...credential, district: value }))
                  }
                  errorMessage={credentialsFormErrors.district}
                />
              </Grid>
              <Grid xs={12} md={4} padding={1}>
                <Input
                  type='text'
                  margin='normal'
                  name='city'
                  label='Cidade'
                  value={credential.city}
                  onChange={(value) =>
                    dispatch(setCredentials({ ...credential, city: value }))
                  }
                  errorMessage={credentialsFormErrors.city}
                />
              </Grid>
              <Grid xs={12} md={4} padding={1}>
                <Input
                  type='text'
                  margin='normal'
                  name='state'
                  label='Estado'
                  value={credential.state}
                  onChange={(value) =>
                    dispatch(setCredentials({ ...credential, state: value }))
                  }
                  errorMessage={credentialsFormErrors.state}
                />
              </Grid>
            </Grid>
          </Paper>
          <Paper style={{ width: '100%', marginTop: 20, marginBottom: 15 }}>
            <Grid container padding={3}>
              <Typography variant='h5' color={theme.palette.text.main}>
                Informações da credencial
              </Typography>
            </Grid>
            <Grid container>
              <Grid xs={12} md={6} padding={1}>
                <Input
                  type='text'
                  margin='normal'
                  label={
                    location.pathname == CREDENTIALS_CREATE
                      ? 'Número (gerado automaticamente)'
                      : 'Número'
                  }
                  disabled={location.pathname == CREDENTIALS_CREATE}
                  value={
                    location.pathname == CREDENTIALS_CREATE
                      ? ''
                      : credential.number.toString()
                  }
                />
              </Grid>
              <Grid xs={12} md={6} padding={1}>
                <DatePicker
                  label='Data de validade'
                  value={
                    credential.expirationDate
                      ? moment(credential.expirationDate).format('YYYY-MM-DD')
                      : ''
                  }
                  onChange={(value) =>
                    dispatch(
                      setCredentials({ ...credential, expirationDate: value }),
                    )
                  }
                  errorMessage={credentialsFormErrors.expirationDate}
                />
              </Grid>
              {/* <Grid xs={12} padding={1}>
                <FileUpload
                  key={uploadFormKey}
                  onUpload={(form, file) => {
                    setFileForm(form);
                    setFile(file);
                    setIsFileUploadModalOpen(true);
                  }}
                />
                {credential.documents && (
                  <Grid
                    xs={12}
                    md={4}
                    paddingRight={responsiveMode == 'phone' ? 2 : 0}
                  >
                    <Button
                      fullWidth
                      color='primary'
                      variant='contained'
                      startIcon={<Download />}
                      label={`Download do arquivo: ${credential.documents}`}
                      onClick={handleDownloadDocument}
                    />
                  </Grid>
                )}
              </Grid> */}
              <Grid xs={12} md={4} padding={1}>
                <Input
                  type='text'
                  margin='normal'
                  name='licensePlate'
                  label='Placa'
                  value={
                    credential.licensePlate
                      ? VMasker.toPattern(
                          credential.licensePlate,
                          'AAA9S99',
                        ).toUpperCase()
                      : ''
                  }
                  onChange={(value) => {
                    let handledValue = value;
                    if (handledValue.length > 7) {
                      handledValue = handledValue.slice(0, -1);
                    }
                    dispatch(
                      setCredentials({
                        ...credential,
                        licensePlate: handledValue,
                      }),
                    );
                  }}
                  errorMessage={credentialsFormErrors.licensePlate}
                />
              </Grid>
              <Grid xs={12} md={4} padding={1}>
                <Input
                  type='text'
                  margin='normal'
                  label='Marca'
                  value={credential.vehicleBrand}
                  onChange={(value) =>
                    dispatch(
                      setCredentials({ ...credential, vehicleBrand: value }),
                    )
                  }
                  errorMessage={credentialsFormErrors.vehicleBrand}
                />
              </Grid>
              <Grid xs={12} md={4} padding={1}>
                <Input
                  type='text'
                  margin='normal'
                  label='Cor'
                  value={credential.vehicleColor}
                  onChange={(value) =>
                    dispatch(
                      setCredentials({ ...credential, vehicleColor: value }),
                    )
                  }
                  errorMessage={credentialsFormErrors.vehicleColor}
                />
              </Grid>
              <Grid xs={12} padding={1}>
                <Checkbox
                  label='Ativar delimitação'
                  checked={!!credential.restricted}
                  onChange={() => {
                    const restricted = credential.restricted == 0 ? 1 : 0;

                    dispatch(
                      setCredentials({
                        ...credential,
                        restricted,
                        routes: restricted === 0 ? [] : credential.routes,
                      }),
                    );
                  }}
                />
              </Grid>
              <Grid xs={12} padding={1}>
                <Input
                  type='text'
                  margin='normal'
                  label='Delimitação'
                  value={credential.observation || ''}
                  onChange={(value) =>
                    dispatch(
                      setCredentials({ ...credential, observation: value }),
                    )
                  }
                  errorMessage={credentialsFormErrors.observation}
                />
              </Grid>
              <Grid xs={12} padding={1}>
                <Autocomplete
                  multiple
                  isOptionEqualToValue={(
                    selected: {
                      id: string;
                      value: string;
                    },
                    comparison: {
                      id: string;
                      value: string;
                    },
                  ) => {
                    return String(selected.id) === String(comparison.id);
                  }}
                  options={(routes || [])
                    .filter((route: any) => route?.id != '0')
                    .map((route: any) => ({
                      id: route.id,
                      value: route.value,
                    }))}
                  getOptionLabel={(option) => option.value}
                  value={(routes || [])
                    .filter((route: any) =>
                      (credential.routes || []).includes(+route.id),
                    )
                    .map((route: any) => ({
                      id: route.id,
                      value: route.value,
                    }))}
                  onChange={(_, value) => {
                    const routesId = value.map((value) => +value.id);
                    dispatch(
                      setCredentials({ ...credential, routes: routesId }),
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant='filled'
                      label='Trecho(s) da delimitação'
                      error={!!credentialsFormErrors.routes}
                      helperText={credentialsFormErrors.routes}
                    />
                  )}
                />
              </Grid>
              <Grid container padding={1}>
                <Grid
                  xs={12}
                  md={location.pathname.startsWith(CREDENTIALS_UPDATE) ? 3 : 6}
                  padding={1}
                >
                  <Button
                    fullWidth
                    variant='contained'
                    label={
                      location.pathname == CREDENTIALS_CREATE
                        ? 'Cadastrar'
                        : 'Editar'
                    }
                    startIcon={<Save />}
                    onClick={handleSubmit}
                  />
                </Grid>
                {location.pathname.startsWith(CREDENTIALS_UPDATE) && (
                  <Grid xs={12} md={3} padding={1}>
                    <Button
                      fullWidth
                      color='error'
                      variant='contained'
                      label='Desativar'
                      startIcon={<Cancel />}
                      onClick={() => handleDeactivateCredential()}
                    />
                  </Grid>
                )}
                {location.pathname.startsWith(CREDENTIALS_UPDATE) && (
                  <Grid xs={12} md={3} padding={1}>
                    <Button
                      fullWidth
                      color='secondary'
                      variant='contained'
                      label='Renovar'
                      startIcon={<Loop />}
                      onClick={() => setIsRenewModalOpen(!isRenewModalOpen)}
                    />
                  </Grid>
                )}
                <Grid
                  xs={12}
                  md={location.pathname.startsWith(CREDENTIALS_UPDATE) ? 3 : 6}
                  padding={1}
                >
                  <Button
                    fullWidth
                    color='error'
                    variant='contained'
                    label='Voltar'
                    startIcon={<ArrowBack />}
                    onClick={() => navigate(CREDENTIALS_MANAGEMENT)}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </>
      )}
      <DeactivateCredentialModal />
    </PageWrapper>
  );
};
